import React, { useEffect } from 'react';
import { connectLean } from 'lean-redux';
import { withApollo } from 'react-apollo';
import {
  getAssignmentsOverviewStudent,
  getAssignmentsOverviewTeacher,
} from 'legacy/packages/unit-outline/src/queries';
import log from 'chameleon/ui-stack/utilities/log';
import { forEach, isEmpty, merge, get } from 'lodash';

const connectLeanMethods = {
  scope: 'units',
  /**
   * Set shared assignment overview for each resource
   * @param {array} assignments List of assignment overview details
   * @param {object} assessmentsToUnits List of assessments and
   * related units/resources location by assessmentId
   * */
  setResourcesAssignmentsOverview(
    assignmentOverviews,
    assessmentsToUnits,
    assignmentStatuses,
    subjectId,
    sppLearnosityIds,
  ) {
    let units = {
      assignmentStatusesUpdateDate: new Date(),
      areAssignmentStatusesLoaded: true,
    };
    const newAssessments = {};

    const hasStatuses = !!assignmentStatuses;
    const assignments = hasStatuses ? assignmentStatuses : assignmentOverviews;
    assignments.forEach((assignment) => {
      // skip SPPs, they will go into a different key
      if (sppLearnosityIds.includes(String(assignment.assessmentId))) {
        return;
      }

      let assignmentOverview = {};
      let assignmentStatus = { isAssigned: true };
      if (hasStatuses) {
        assignmentStatus = assignment;
        assignmentOverview =
          assignments.find(
            ({ assessmentId }) => assessmentId === assignment.assessmentId,
          ) || {};
      } else {
        assignmentOverview = assignment;
      }
      const unitsConfig = assessmentsToUnits[assignment.assessmentId] || {};
      const { unitId, resourceId } = unitsConfig;
      if (!isEmpty(unitsConfig)) {
        if (isEmpty(units[`unit-${unitId}`])) {
          units[`unit-${unitId}`] = {
            resources: {},
          };
        }

        newAssessments[assignment.assessmentId] = 1;
        const resourceKey = `resource-${resourceId}-subject-${subjectId}`;
        units[`unit-${unitId}`].resources[resourceKey] = {
          /**
           * Used to force refresh
           * */
          test: 'overview',
          updateTimestamp: new Date(),
          assignmentOverview: {
            ...assignmentOverview,
            assignments: [
              ...get(
                units,
                [
                  `unit-${unitId}`,
                  'resources',
                  resourceKey,
                  'assignmentOverview',
                  'assignments',
                ],
                [],
              ),
              ...[assignment],
            ],
          },
          ...assignmentStatus,
        };
      }
    });

    /**
     * Unassign assignment resources when not found in backend response
     * */
    forEach(assessmentsToUnits, (asessmentConfig, assessmentId) => {
      if (!newAssessments[assessmentId]) {
        const { unitId, resourceId } = asessmentConfig;
        if (units[`unit-${unitId}`]) {
          units[`unit-${unitId}`].resources[
            `resource-${resourceId}-subject-${subjectId}`
          ] = {
            isAssigned: false,
            assignmentOverview: null,
          };
        }
      }
    });

    this.setState((prevState) => merge(prevState, units));
  },
  /**
   * Reset shared assignment overview for resources for a student
   * */
  resetResourcesAssignmentsOverview() {
    this.setState((prevState) => {
      let units = prevState;
      forEach(units, (unit, unitId) => {
        forEach(unit.resources, (resource, resourceId) => {
          units[unitId].resources[resourceId] = {
            ...resource,
            assignmentOverview: null,
          };
        });
      });

      return units;
    });
  },
  /**
   * Reset shared spp assignments for resources for a student
   * */
  resetResourcesSppAssignments() {
    this.setState((prevState) => {
      let units = prevState;
      forEach(units, (unit, unitId) => {
        forEach(unit.resources, (resource, resourceId) => {
          units[unitId].resources[resourceId] = {
            ...resource,
            sppAssignments: null,
          };
        });
      });

      return units;
    });
  },
  /**
   * Reset shared assign responses to remove duplicates in View All button
   * TeacherAssignments is providing data for the new assignment
   * */
  resetResourcesAssignResponses() {
    this.setState((prevState) => {
      let units = prevState;
      forEach(units, (unit, unitId) => {
        forEach(unit.resources, (resource, resourceId) => {
          units[unitId].resources[resourceId] = {
            ...resource,
            assignResponses: [],
          };
        });
      });

      return units;
    });
  },
  /**
   * Student Practice assignments are added to the units scope here
   * @param {array} assignments List of SPP assignment details
   * @param assessmentsToUnits
   * @param subjectId
   * @param sppLearnosityIds
   * */
  setSPPAssignments(
    assignments,
    assessmentsToUnits,
    subjectId,
    sppLearnosityIds,
  ) {
    let units = {
      assignmentStatusesUpdateDate: new Date(),
      areAssignmentStatusesLoaded: true,
    };

    assignments.forEach((assignment) => {
      const assessmentId = get(assignment, 'assessmentId');

      // skip PEs and PPCs, they will go into a different key
      if (!sppLearnosityIds.includes(String(assessmentId))) {
        return;
      }

      const unitsConfig = assessmentsToUnits[assessmentId] || {};
      const { unitId, resourceId } = unitsConfig;
      if (!isEmpty(unitsConfig)) {
        if (isEmpty(units[`unit-${unitId}`])) {
          units[`unit-${unitId}`] = {
            resources: {},
          };
        }

        const resourceKey = `resource-${resourceId}-subject-${subjectId}`;
        units[`unit-${unitId}`].resources[resourceKey] = {
          /**
           * Used to force refresh
           * */
          assessmentId,
          isAssigned: assignment.isAssigned,
          updateTimestamp: new Date(),
          sppAssignments: {
            [assessmentId]: [
              ...get(
                units,
                [
                  `unit-${unitId}`,
                  'resources',
                  resourceKey,
                  'sppAssignments',
                  assessmentId,
                ],
                [],
              ),
              ...[assignment],
            ],
          },
        };
      }
    });

    this.setState((prevState) => merge(prevState, units));
  },
};

const AssignHelper = (WrappedComponent) => {
  const Assign = (props) => {
    const {
      params: { subject },
      courseOutline: { loading, units = [] } = {},
      userData: { chosenEducationPeriodCode },
      resetResourcesAssignResponses,
      resetResourcesAssignmentsOverview,
      resetResourcesSppAssignments,
      impersonating: { isImpersonating = false } = {},
    } = props;

    useEffect(
      () => () => {
        resetResourcesAssignmentsOverview();
        resetResourcesSppAssignments();
      },
      [isImpersonating],
    );
    useEffect(() => () => resetResourcesAssignResponses(), []);

    useEffect(() => {
      /**
       * Load assignment overview
       * @param {string} subjectId The subject id
       * @param {object} rawUnits Unit items list
       * */
      const loadAssignmentsOverview = async (subjectId, rawUnits) => {
        let assessmentIds = [];
        let sppLearnosityIds = [];
        let assessmentsToUnits = {};
        (rawUnits || []).forEach((unit) => {
          (unit.subunits || []).forEach((subunit) => {
            ([...subunit.resources] || []).forEach((resource) => {
              const { resourceId, assessmentId } = resource;
              // Check if assessmentId exists and assessmentId is a number.
              if (assessmentId && parseInt(assessmentId, 10)) {
                assessmentIds.push(assessmentId);
                assessmentsToUnits[assessmentId] = {
                  assessmentId,
                  unitId: unit.unitId,
                  resourceId,
                };
              } else if (assessmentId) {
                // We are throwing error for resources that are assessment only.
                // console.warn is used here so we can know  assessmentIds that are not valid
                // Without breaking the remaing flow to fetch data for the valid ones.
                // eslint-disable-next-line no-console
                console.warn(
                  `Assessment with id: ${assessmentId} is not a number.`,
                );
              }

              if (subunit.type === 'SPP') {
                sppLearnosityIds.push(String(assessmentId));
              }
            });
          });
        });
        const {
          resetResourcesAssignmentsOverview,
          setResourcesAssignmentsOverview,
          setSPPAssignments,
          client,
          userData: { isStudent },
        } = props;
        let response;
        try {
          const variables = {
            subjectId: Number(subjectId),
            assessmentIds: assessmentIds.map((assessmentId) =>
              Number.parseInt(assessmentId, 10),
            ),
          };

          if (!isStudent && chosenEducationPeriodCode) {
            variables.educationPeriod = chosenEducationPeriodCode;
          }

          response = await client.query({
            query: isStudent
              ? getAssignmentsOverviewStudent
              : getAssignmentsOverviewTeacher,
            fetchPolicy: 'network-only',
            variables,
          });
        } catch (error) {
          log('error', error);
        }

        if (response) {
          const { assignmentsOverviewStudent, assignmentStatuses } =
            response.data || {};

          const assignments = isStudent
            ? assignmentsOverviewStudent.assignments
            : assignmentStatuses;

          const assignmentsLength = (assignments || []).length;

          if (assignments && assignmentsLength && sppLearnosityIds.length) {
            setSPPAssignments(
              assignments,
              assessmentsToUnits,
              subjectId,
              sppLearnosityIds,
            );
          }

          if (assignmentsLength) {
            setResourcesAssignmentsOverview(
              assignments,
              assessmentsToUnits,
              assignmentStatuses,
              subjectId,
              sppLearnosityIds,
            );
          } else if (isStudent && !assignmentsLength) {
            /**
             * @edge case
             * For students when assignments list is empty
             * Reset assignment resources status
             * */
            resetResourcesAssignmentsOverview();
          }
        }
      };

      if (!loading) {
        loadAssignmentsOverview(subject, units);
      }
    }, [loading]);

    return <WrappedComponent key="assign-helper" {...props} />;
  };

  return connectLean(connectLeanMethods)(withApollo(Assign));
};

export default AssignHelper;
